import { useEffect, useState, type FormEvent } from 'react';

import { Feedback } from './Feedback';
import {
  addWebhook,
  deleteWebhook,
  fetchIntegrations,
  regenerateModToken,
  saveTwitchIntegration,
  testTwitchIntegration,
  testWebhooks,
  type IntegrationsConfig,
  type WebhookFormat,
} from '../services/api';

/**
 * Aba "Integrações" da dashboard — os caminhos SEM OBS:
 *   • Página do moderador (tempo real no celular/segundo monitor)
 *   • Webhooks de saída (Discord nativo; Zapier/Make para o resto)
 *   • Bot de chat da Twitch
 *   • YouTube/Kick/Instagram: em breve (dependem de credenciais externas)
 */
export function IntegrationsTab() {
  const [config, setConfig] = useState<IntegrationsConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Formulário de novo webhook
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookFormat, setWebhookFormat] = useState<WebhookFormat>('json');

  // Formulário da Twitch
  const [twitchEnabled, setTwitchEnabled] = useState(false);
  const [twitchChannel, setTwitchChannel] = useState('');
  const [twitchBot, setTwitchBot] = useState('');
  const [twitchToken, setTwitchToken] = useState('');
  const [twitchHasToken, setTwitchHasToken] = useState(false);

  useEffect(() => {
    fetchIntegrations()
      .then((data) => {
        setConfig(data);
        setTwitchEnabled(data.twitch.enabled);
        setTwitchChannel(data.twitch.channel);
        setTwitchBot(data.twitch.botUsername);
        setTwitchHasToken(data.twitch.hasToken);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : 'Erro ao carregar as integrações.'),
      );
  }, []);

  if (error && !config) return <Feedback kind="error">{error}</Feedback>;
  if (!config) return <p className="page-loading">Carregando…</p>;

  const modUrl = `${window.location.origin}/mod/${config.modToken}`;

  function refresh(data: IntegrationsConfig, message: string) {
    setConfig(data);
    setTwitchEnabled(data.twitch.enabled);
    setTwitchChannel(data.twitch.channel);
    setTwitchBot(data.twitch.botUsername);
    setTwitchHasToken(data.twitch.hasToken);
    setError(null);
    setNotice(message);
  }

  function showError(err: unknown, fallback: string) {
    setNotice(null);
    setError(err instanceof Error ? err.message : fallback);
  }

  async function copyModUrl() {
    try {
      await navigator.clipboard.writeText(modUrl);
      setNotice('URL copiada! Envie apenas para moderadores de confiança.');
    } catch {
      setNotice('Não foi possível copiar automaticamente — selecione e copie a URL acima.');
    }
  }

  async function regenerateMod() {
    const confirmed = window.confirm(
      'Regenerar invalida a URL atual da página do moderador. Quem tiver a URL antiga perde o acesso. Continuar?',
    );
    if (!confirmed) return;
    try {
      refresh(await regenerateModToken(), 'Nova URL do moderador gerada.');
    } catch (err) {
      showError(err, 'Erro ao regenerar a URL.');
    }
  }

  async function handleAddWebhook(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      refresh(await addWebhook(webhookUrl, webhookFormat), 'Webhook cadastrado!');
      setWebhookUrl('');
    } catch (err) {
      showError(err, 'Erro ao cadastrar o webhook.');
    }
  }

  async function handleDeleteWebhook(id: string) {
    try {
      refresh(await deleteWebhook(id), 'Webhook removido.');
    } catch (err) {
      showError(err, 'Erro ao remover o webhook.');
    }
  }

  async function handleTestWebhooks() {
    try {
      await testWebhooks();
      setError(null);
      setNotice('Teste disparado para todos os webhooks ativos! Confira o destino (ex.: Discord).');
    } catch (err) {
      showError(err, 'Erro ao disparar o teste.');
    }
  }

  async function handleSaveTwitch(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      refresh(
        await saveTwitchIntegration({
          enabled: twitchEnabled,
          channel: twitchChannel,
          botUsername: twitchBot,
          oauthToken: twitchToken,
        }),
        'Configuração da Twitch salva!',
      );
      setTwitchToken('');
    } catch (err) {
      showError(err, 'Erro ao salvar a configuração da Twitch.');
    }
  }

  async function handleTestTwitch() {
    try {
      await testTwitchIntegration();
      setError(null);
      setNotice('Mensagem de teste enviada no chat da Twitch!');
    } catch (err) {
      showError(err, 'Erro ao testar a Twitch.');
    }
  }

  return (
    <section aria-label="Integrações sem OBS" className="overlay-config">
      {notice && <Feedback kind="success">{notice}</Feedback>}
      {error && <Feedback kind="error">{error}</Feedback>}

      {/* ---- Página do moderador ---- */}
      <div className="card">
        <h2 className="section-title section-title--first">Página do moderador</h2>
        <p className="overlay-config__text">
          Não usa OBS? Abra esta página no celular ou num segundo monitor: as mensagens chegam em
          tempo real (com som opcional) e você — ou um moderador de confiança — pode marcá-las como
          lidas ou ocultá-las durante a live.
        </p>

        <label className="form__label" htmlFor="mod-url">
          URL da página do moderador (mantenha em segredo)
        </label>
        <div className="overlay-config__url-row">
          <input
            className="form__input overlay-config__url"
            id="mod-url"
            type="text"
            readOnly
            value={modUrl}
            onFocus={(event) => event.target.select()}
          />
          <button className="button button--small" type="button" onClick={copyModUrl}>
            Copiar
          </button>
        </div>

        <div className="overlay-config__actions">
          <a className="button" href={modUrl} target="_blank" rel="noreferrer">
            Abrir página do moderador
          </a>
          <button className="button button--ghost" type="button" onClick={regenerateMod}>
            Regenerar URL
          </button>
        </div>
      </div>

      {/* ---- Webhooks ---- */}
      <div className="card">
        <h2 className="section-title section-title--first">Webhooks (Discord, Zapier, bots…)</h2>
        <p className="overlay-config__text">
          A cada mensagem recebida, a plataforma envia um POST para as URLs abaixo. Escolha o
          formato <strong>Discord</strong> para colar direto a URL de um webhook de canal do
          Discord; o formato <strong>JSON</strong> serve para Zapier/Make ou bots próprios — é o
          caminho para levar os alertas a plataformas sem API oficial (Kick, Instagram…).
        </p>

        {config.webhooks.length > 0 && (
          <ul className="webhook-list">
            {config.webhooks.map((webhook) => (
              <li key={webhook.id} className="webhook-list__item">
                <div className="webhook-list__info">
                  <span className="webhook-list__url">{webhook.url}</span>
                  <span className="webhook-list__format">
                    {webhook.format === 'discord' ? 'Discord' : 'JSON'}
                  </span>
                </div>
                <button
                  className="button button--small button--ghost"
                  type="button"
                  onClick={() => handleDeleteWebhook(webhook.id)}
                >
                  Remover
                </button>
              </li>
            ))}
          </ul>
        )}

        <form className="webhook-form" onSubmit={handleAddWebhook}>
          <div className="form__field">
            <label className="form__label" htmlFor="webhook-url">
              URL do webhook
            </label>
            <input
              className="form__input"
              id="webhook-url"
              type="url"
              required
              placeholder="https://discord.com/api/webhooks/…"
              value={webhookUrl}
              onChange={(event) => setWebhookUrl(event.target.value)}
            />
          </div>
          <div className="form__field">
            <label className="form__label" htmlFor="webhook-format">
              Formato
            </label>
            <select
              className="form__input"
              id="webhook-format"
              value={webhookFormat}
              onChange={(event) => setWebhookFormat(event.target.value as WebhookFormat)}
            >
              <option value="json">JSON completo (Zapier, Make, bots próprios)</option>
              <option value="discord">Discord (mensagem pronta no canal)</option>
            </select>
          </div>
          <div className="overlay-config__actions">
            <button className="button button--primary" type="submit">
              Adicionar webhook
            </button>
            {config.webhooks.length > 0 && (
              <button className="button button--ghost" type="button" onClick={handleTestWebhooks}>
                Testar webhooks
              </button>
            )}
          </div>
        </form>
      </div>

      {/* ---- Twitch ---- */}
      <form className="card form" onSubmit={handleSaveTwitch}>
        <h2 className="section-title section-title--first">Bot de chat da Twitch</h2>
        <p className="overlay-config__text">
          Publica cada mensagem recebida no chat da sua live na Twitch, como faz o LivePix. Use a
          sua conta ou uma conta separada de bot; o token OAuth precisa do escopo de chat (
          <code>chat:edit</code>) e pode ser gerado em geradores como o Twitch Token Generator.
        </p>

        <label className="form__label form__label--checkbox">
          <input
            type="checkbox"
            checked={twitchEnabled}
            onChange={(event) => setTwitchEnabled(event.target.checked)}
          />{' '}
          Ativar bot da Twitch
        </label>

        <div className="form__field">
          <label className="form__label" htmlFor="twitch-channel">
            Canal da live
          </label>
          <input
            className="form__input"
            id="twitch-channel"
            type="text"
            placeholder="meucanal"
            value={twitchChannel}
            onChange={(event) => setTwitchChannel(event.target.value)}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="twitch-bot">
            Usuário do bot
          </label>
          <input
            className="form__input"
            id="twitch-bot"
            type="text"
            placeholder="meubot (ou o seu próprio usuário)"
            value={twitchBot}
            onChange={(event) => setTwitchBot(event.target.value)}
          />
        </div>

        <div className="form__field">
          <label className="form__label" htmlFor="twitch-token">
            Token OAuth do bot
          </label>
          <input
            className="form__input"
            id="twitch-token"
            type="password"
            placeholder={twitchHasToken ? '•••••• (token salvo — preencha para trocar)' : 'oauth:…'}
            value={twitchToken}
            onChange={(event) => setTwitchToken(event.target.value)}
          />
          <p className="form__help">
            O token fica guardado no servidor e nunca é reexibido. Deixe em branco para manter o
            atual.
          </p>
        </div>

        <div className="overlay-config__actions">
          <button className="button button--primary" type="submit">
            Salvar configuração
          </button>
          {twitchHasToken && (
            <button className="button button--ghost" type="button" onClick={handleTestTwitch}>
              Testar no chat
            </button>
          )}
        </div>
      </form>

      {/* ---- Em breve ---- */}
      <div className="card">
        <h2 className="section-title section-title--first">YouTube, Kick e Instagram</h2>
        <p className="overlay-config__text">
          <strong>YouTube</strong>: o chat ao vivo exige um app OAuth aprovado no Google Cloud —
          entra assim que as credenciais do projeto estiverem criadas. <strong>Kick</strong> e{' '}
          <strong>Instagram</strong> ainda não têm API oficial de chat. Enquanto isso, os webhooks
          acima (via Zapier/Make ou um bot próprio) e a página do moderador cobrem essas lives — e
          novas integrações nativas chegam sem você precisar mudar nada.
        </p>
      </div>
    </section>
  );
}
